import { useEffect, useRef, useCallback } from "react";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  type: "heart" | "wallet" | "person" | "shield" | "plus";
  opacity: number;
  pulsePhase: number;
  pulseSpeed: number;
}

const NODE_COUNT = 18;
const CONNECTION_DISTANCE = 160;
const PULSE_GLOW_INTERVAL = 3000;

function drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  ctx.beginPath();
  const s = size * 0.55;
  ctx.moveTo(x, y + s * 0.3);
  ctx.bezierCurveTo(x, y - s * 0.3, x - s, y - s * 0.3, x - s, y + s * 0.1);
  ctx.bezierCurveTo(x - s, y + s * 0.6, x, y + s, x, y + s);
  ctx.bezierCurveTo(x, y + s, x + s, y + s * 0.6, x + s, y + s * 0.1);
  ctx.bezierCurveTo(x + s, y - s * 0.3, x, y - s * 0.3, x, y + s * 0.3);
  ctx.fill();
}

function drawPerson(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  const s = size * 0.4;
  ctx.beginPath();
  ctx.arc(x, y - s * 0.5, s * 0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y + s * 0.7, s * 0.8, Math.PI, 0);
  ctx.fill();
}

function drawWallet(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  const s = size * 0.45;
  const r = s * 0.15;
  ctx.beginPath();
  ctx.roundRect(x - s, y - s * 0.65, s * 2, s * 1.3, r);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + s * 0.55, y, s * 0.2, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.fill();
}

function drawShield(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  const s = size * 0.5;
  ctx.beginPath();
  ctx.moveTo(x, y - s);
  ctx.lineTo(x + s * 0.8, y - s * 0.5);
  ctx.quadraticCurveTo(x + s * 0.8, y + s * 0.6, x, y + s);
  ctx.quadraticCurveTo(x - s * 0.8, y + s * 0.6, x - s * 0.8, y - s * 0.5);
  ctx.closePath();
  ctx.fill();
}

function drawPlus(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  const s = size * 0.35;
  const t = s * 0.35;
  ctx.beginPath();
  ctx.roundRect(x - s, y - t, s * 2, t * 2, t * 0.3);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(x - t, y - s, t * 2, s * 2, t * 0.3);
  ctx.fill();
}

function drawIcon(ctx: CanvasRenderingContext2D, node: Node) {
  switch (node.type) {
    case "heart": drawHeart(ctx, node.x, node.y, node.radius); break;
    case "person": drawPerson(ctx, node.x, node.y, node.radius); break;
    case "wallet": drawWallet(ctx, node.x, node.y, node.radius); break;
    case "shield": drawShield(ctx, node.x, node.y, node.radius); break;
    case "plus": drawPlus(ctx, node.x, node.y, node.radius); break;
  }
}

export function HeroAnimation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<Node[]>([]);
  const animRef = useRef<number>(0);
  const pulseTimeRef = useRef(0);
  const activeConnectionRef = useRef<{ from: number; to: number; progress: number; opacity: number } | null>(null);

  const initNodes = useCallback((w: number, h: number) => {
    const types: Node["type"][] = ["heart", "wallet", "person", "shield", "plus"];
    const nodes: Node[] = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: 10 + Math.random() * 8,
        type: types[i % types.length],
        opacity: 0.15 + Math.random() * 0.2,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.3 + Math.random() * 0.4,
      });
    }
    nodesRef.current = nodes;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w: number, h: number;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.parentElement?.getBoundingClientRect();
      w = rect?.width || window.innerWidth;
      h = rect?.height || 600;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (nodesRef.current.length === 0) initNodes(w, h);
    };

    resize();
    window.addEventListener("resize", resize);

    const animate = (timestamp: number) => {
      ctx.clearRect(0, 0, w, h);
      const nodes = nodesRef.current;

      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < -20) node.x = w + 20;
        if (node.x > w + 20) node.x = -20;
        if (node.y < -20) node.y = h + 20;
        if (node.y > h + 20) node.y = -20;
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DISTANCE) {
            const alpha = (1 - dist / CONNECTION_DISTANCE) * 0.08;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      if (timestamp - pulseTimeRef.current > PULSE_GLOW_INTERVAL) {
        pulseTimeRef.current = timestamp;
        const from = Math.floor(Math.random() * nodes.length);
        let to = from;
        let bestDist = Infinity;
        for (let j = 0; j < nodes.length; j++) {
          if (j === from) continue;
          const dx = nodes[from].x - nodes[j].x;
          const dy = nodes[from].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECTION_DISTANCE && d < bestDist) {
            bestDist = d;
            to = j;
          }
        }
        if (to !== from) {
          activeConnectionRef.current = { from, to, progress: 0, opacity: 1 };
        }
      }

      const ac = activeConnectionRef.current;
      if (ac) {
        ac.progress += 0.015;
        if (ac.progress > 1) ac.opacity -= 0.03;
        if (ac.opacity <= 0) {
          activeConnectionRef.current = null;
        } else {
          const n1 = nodes[ac.from];
          const n2 = nodes[ac.to];
          const p = Math.min(ac.progress, 1);

          const grad = ctx.createLinearGradient(n1.x, n1.y, n2.x, n2.y);
          grad.addColorStop(0, `rgba(110, 231, 183, ${0.6 * ac.opacity})`);
          grad.addColorStop(p, `rgba(110, 231, 183, ${0.8 * ac.opacity})`);
          grad.addColorStop(Math.min(p + 0.01, 1), `rgba(110, 231, 183, 0)`);
          grad.addColorStop(1, `rgba(110, 231, 183, 0)`);

          ctx.beginPath();
          ctx.moveTo(n1.x, n1.y);
          ctx.lineTo(n2.x, n2.y);
          ctx.strokeStyle = grad;
          ctx.lineWidth = 2;
          ctx.stroke();

          const dotX = n1.x + (n2.x - n1.x) * p;
          const dotY = n1.y + (n2.y - n1.y) * p;
          ctx.beginPath();
          ctx.arc(dotX, dotY, 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(110, 231, 183, ${ac.opacity})`;
          ctx.fill();

          if (ac.progress >= 1) {
            ctx.beginPath();
            ctx.arc(n2.x, n2.y, n2.radius + 6, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(110, 231, 183, ${0.15 * ac.opacity})`;
            ctx.fill();
          }
        }
      }

      nodes.forEach(node => {
        const pulse = Math.sin(timestamp * 0.001 * node.pulseSpeed + node.pulsePhase);
        const dynamicOpacity = node.opacity + pulse * 0.06;

        ctx.fillStyle = `rgba(255, 255, 255, ${dynamicOpacity})`;
        drawIcon(ctx, node);

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius + 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${dynamicOpacity * 0.15})`;
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [initNodes]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
      data-testid="canvas-hero-animation"
    />
  );
}
